import { ChevronRight, Image, LoaderCircle, Plus, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useStore } from '../hooks/use-store';
import { t, type MessageKey } from '../i18n';
import { $currentUser } from '../store/app.store';
import type { BgmCurrentUser, SubjectCollection, SubjectCollectionPage } from '../types/bgm';
import { safeUrl } from '../utils/url';
import { useApp } from './app-context';
import { Button } from './button';
import { Select } from './select';

type CollectionTimelineVariant = 'home' | 'menu' | 'page';
type CollectionType = SubjectCollection['type'];

const collectionTypes: CollectionType[] = [1, 2, 3, 4, 5];

const collectionLabels: Record<SubjectCollection['type'], MessageKey> = {
  1: 'collectionWish',
  2: 'collectionDone',
  3: 'collectionDoing',
  4: 'collectionOnHold',
  5: 'collectionDropped',
};

const pageSizes: Record<CollectionTimelineVariant, number> = { home: 8, menu: 8, page: 30 };

const dateKey = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'unknown' : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export const CollectionTimeline = ({
  onNavigate,
  variant,
}: {
  onNavigate?: () => void;
  variant: CollectionTimelineVariant;
}) => {
  const { bgmAuth, locale, navigate, showToast } = useApp();
  const currentUser = useStore($currentUser);
  const [items, setItems] = useState<SubjectCollection[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const [selectedType, setSelectedType] = useState<CollectionType | 0>(0);
  const username = useRef<string | undefined>(undefined);
  const limit = pageSizes[variant];

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void (async () => {
      username.current ??= (await bgmAuth<BgmCurrentUser>('/v0/me')).username;
      const typeQuery = variant === 'page' && selectedType ? `&type=${selectedType}` : '';
      const result = await bgmAuth<SubjectCollectionPage>(
        `/v0/users/${encodeURIComponent(username.current)}/collections?subject_type=2${typeQuery}&limit=${limit}`,
      );
      if (active) {
        setItems(
          [...result.data].sort(
            (left, right) => new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime(),
          ),
        );
        setTotal(result.total);
      }
    })()
      .catch(() => {
        if (active) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [bgmAuth, limit, revision, selectedType, variant]);

  const groups = useMemo(() => {
    const grouped: Array<{ key: string; date: Date | undefined; items: SubjectCollection[] }> = [];
    for (const item of items) {
      const key = dateKey(item.updated_at);
      const current = grouped.at(-1);
      if (current?.key === key) {
        current.items.push(item);
      } else {
        const date = new Date(item.updated_at);
        grouped.push({ key, date: Number.isNaN(date.getTime()) ? undefined : date, items: [item] });
      }
    }
    return grouped;
  }, [items]);

  const openSubject = (subjectId: number): void => {
    onNavigate?.();
    navigate(`/subjects/${subjectId}`);
  };
  const openMore = (): void => {
    onNavigate?.();
    navigate('/collection-records');
  };
  const loadMore = async (): Promise<void> => {
    if (!username.current || loadingMore || items.length >= total) {
      return;
    }
    setLoadingMore(true);
    try {
      const typeQuery = variant === 'page' && selectedType ? `&type=${selectedType}` : '';
      const result = await bgmAuth<SubjectCollectionPage>(
        `/v0/users/${encodeURIComponent(username.current)}/collections?subject_type=2${typeQuery}&limit=${limit}&offset=${items.length}`,
      );
      const known = new Set(items.map((item) => item.subject.id));
      const next = [...items, ...result.data.filter((item) => !known.has(item.subject.id))].sort(
        (left, right) => new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime(),
      );
      setItems(next);
      setTotal(result.total);
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const content = loading ? (
    <div className="grid min-h-40 place-items-center text-(--muted)">
      <LoaderCircle className="size-5 spin" />
    </div>
  ) : failed ? (
    <div className="grid min-h-40 place-items-center gap-3 text-center text-xs text-(--muted)">
      <span>{currentUser?.bgmToken ? t('requestFailedRetryLater') : t('contentUnavailable')}</span>
      <Button compact onClick={() => setRevision((value) => value + 1)}>
        <RefreshCw className="size-3.5!" />
        {t('refresh')}
      </Button>
    </div>
  ) : items.length === 0 ? (
    <div className="grid min-h-40 place-items-center text-xs text-(--muted)">{t('noCollectionRecords')}</div>
  ) : (
    <div className="grid gap-5">
      {groups.map((group) => (
        <section className="grid grid-cols-[5rem_minmax(0,1fr)] gap-3 max-[560px]:grid-cols-1" key={group.key}>
          <time className="pt-2 text-xs font-bold text-(--muted)">
            {group.date
              ? new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', weekday: 'short' }).format(group.date)
              : t('dateUnknown')}
          </time>
          <div className="relative grid gap-2 border-l border-(--line) pl-4 max-[560px]:ml-1.5">
            {group.items.map((item) => {
              const subject = item.subject;
              const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
              const updatedAt = new Date(item.updated_at);
              return (
                <Button
                  variant="plain"
                  className="relative flex min-w-0 cursor-pointer items-center gap-3 rounded-xl border border-transparent bg-(--surface-2) p-2 text-left transition-colors hover:border-(--input-accent) focus-visible:border-(--input-accent)"
                  key={`${subject.id}-${item.updated_at}`}
                  onClick={() => openSubject(subject.id)}
                >
                  <span className="absolute top-4 -left-[21px] size-2.5 rounded-full border-2 border-(--surface) bg-(--input-accent)" />
                  <span className="grid size-13 shrink-0 place-items-center overflow-hidden rounded-lg bg-(--surface) text-(--muted)">
                    {image ? (
                      <img
                        className="size-full object-cover"
                        src={image}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <Image className="size-4" />
                    )}
                  </span>
                  <span className="grid min-w-0 flex-1 gap-1">
                    <strong className="truncate text-xs text-(--text)">{subject.name_cn || subject.name}</strong>
                    <small className="text-[10px] text-(--input-accent)">{t(collectionLabels[item.type])}</small>
                    <small className="text-[9px] text-(--muted)">
                      {Number.isNaN(updatedAt.getTime())
                        ? t('dateUnknown')
                        : new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(updatedAt)}
                      {item.ep_status ? ` · EP.${item.ep_status}` : ''}
                    </small>
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-(--muted)" />
                </Button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );

  if (variant === 'menu') {
    return (
      <div className="grid gap-3 overflow-x-hidden pb-2">
        {content}
        {!loading && !failed && items.length > 0 ? (
          <Button compact variant="text" className="justify-center" onClick={openMore}>
            {t('viewMore')}
            <ChevronRight className="size-3.5!" />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <section className={variant === 'home' ? 'wall-section' : ''}>
      {variant === 'home' ? (
        <header>
          <h2>{t('collectionRecords')}</h2>
          <Button variant="text" onClick={openMore}>
            {t('viewMore')}
            <ChevronRight />
          </Button>
        </header>
      ) : null}
      {variant === 'page' ? (
        <div className="mb-6 grid max-w-52 gap-2 text-xs font-bold text-(--muted) max-[700px]:w-full max-[700px]:max-w-none">
          <span>{t('collectionStatusFilter')}</span>
          <Select
            ariaLabel={t('collectionStatusFilter')}
            value={selectedType}
            onChange={(value) => setSelectedType(Number(value) as CollectionType | 0)}
            options={[
              { label: t('collectionAll'), value: '0' },
              ...collectionTypes.map((type) => ({ label: t(collectionLabels[type]), value: String(type) })),
            ]}
          />
        </div>
      ) : null}
      {content}
      {!loading && !failed && items.length < total ? (
        <div className="mt-7 flex justify-center">
          <Button loading={loadingMore} onClick={() => void loadMore()}>
            <Plus />
            {t('loadMore')}
          </Button>
        </div>
      ) : null}
    </section>
  );
};
